import { signalStore, withMethods, withState } from '@ngrx/signals';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import {
  removeAllEntities,
  removeEntity,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import {
  Conversation,
  Participant,
  ParticipantPatchRequest,
  ParticipantPostRequest,
} from '@clientwire/api';
import { inject } from '@angular/core';
import { AppTypeService, WireApiService } from '../services';

import { FormControl } from '@angular/forms';
import { FilesToUpload } from '@akostm/orbit-ui';
import * as uuid from 'uuid';
import { ErrorStore } from './error.store';

export interface ParticipantWithSourceId extends Participant {
  sourceId?: string;
}

export interface ActiveConversationEntityState extends Conversation {
  scrolledToBottom?: boolean;
  files: FilesToUpload[];
  messageSending: boolean;
  messageInput: FormControl<string | null>;
  participants?: ParticipantWithSourceId[] | null;
  messageSourceId: string;
}

export const ActiveConversationStore = signalStore(
  { providedIn: 'root', protectedState: false },
  withDevtools('active-conversation-store'),
  withState<{
    error: any;
    inFocusConversationId: Conversation['id'] | null;
  }>({
    error: null,
    inFocusConversationId: null,
  }),
  withEntities<ActiveConversationEntityState>(),
  withMethods((store) => {
    const appType = inject(AppTypeService);
    const wireApiService = inject(WireApiService);
    const errorStore = inject(ErrorStore);
    const activeConversationsName = `wire-${appType.type()}.active_conversations`;

    return {
      removeConversation(conversationId: string) {
        updateState(
          store,
          'Remove conversation from ActiveConversationStore',
          removeEntity(conversationId)
        );
        this.storeActiveConversations();
      },

      storeActiveConversations() {
        // Save the opened conversation ids to local storage
        const openedConversationIds = store.ids();
        localStorage.setItem(activeConversationsName, openedConversationIds.join(','));
        this.setFirstConversationToFocusIfNull();
      },

      setFirstConversationToFocusIfNull() {
        if (!store.inFocusConversationId()) {
          const firstConversation = store.entities()[0];
          if (firstConversation) {
            this.setConversationIntoFocus(firstConversation.id);
          }
        }
      },

      setConversationIntoFocus(conversationId: string) {
        updateState(store, 'Set conversation into focus', {
          inFocusConversationId: conversationId,
        });
      },

      setMessageInputValue(conversationId: string, value: string) {
        const conversation = store.entityMap()[conversationId];
        if (conversation) {
          conversation.messageInput.setValue(value);
        }
      },

      setNewMessageSourceId(conversationId: string, value?: string) {
        if (!value) {
          value = uuid.v4();
        }
        const conversation = store.entityMap()[conversationId];
        if (conversation) {
          conversation.messageSourceId = value;
        }
      },

      swapIdsInLocalStorage(previousIndex: number, newIndex: number) {
        const storeIds = store.ids();
        const currentId = storeIds[previousIndex];
        if (!currentId) throw new Error('No conversation id found at index');
        storeIds.splice(previousIndex, 1);
        storeIds.splice(newIndex, 0, currentId);
        localStorage.setItem(activeConversationsName, storeIds.join(','));
      },

      addConversationToLocalStore(conversationId: string) {
        const activeConversations = this.getStoredConversationIds();
        activeConversations.unshift(conversationId);
        const activeSet = new Set(activeConversations);
        localStorage.setItem(activeConversationsName, Array.from(activeSet).join(','));
      },

      getStoredConversationIds() {
        const storedActiveConversations = localStorage.getItem(activeConversationsName);
        if (!storedActiveConversations) {
          return [];
        }
        return Array.from(new Set(storedActiveConversations.split(',')));
      },

      updateConversation(id: string, changes: Partial<ActiveConversationEntityState>) {
        updateState(
          store,
          'Update conversation in ActiveStoreConversation',
          updateEntity({ id, changes: { ...changes } })
        );
      },

      clearActiveConversations() {
        updateState(store, 'Clear ActiveConversationStore', {
          error: null,
          inFocusConversationId: null,
        });
        updateState(store, 'Clear entities', removeAllEntities());
        this.storeActiveConversations();
      },

      async addParticipantToConversation(
        conversationId: string,
        participantPostRequest: ParticipantPostRequest
      ) {
        const response = await wireApiService.participantsApi.createParticipant({
          conversationId,
          participantPostRequest,
        });

        return response;
      },

      async notifyParticipants(conversationId: string) {
        await wireApiService.participantsApi.notifyClientParticipants({ conversationId });
      },

      async editParticipant(
        conversationId: string,
        participantId: string,
        changeValues: ParticipantPatchRequest
      ) {
        try {
          errorStore.clearErrorsByLocations(['editParticipant']);
          const response = await wireApiService.participantsApi.updateParticipant({
            conversationId,
            participantId,
            participantPatchRequest: changeValues,
          });
          if (response && response.conversationId && response.id) {
            // Edit the participant in the store
            const conversation = { ...store.entityMap()[conversationId] };
            const participants = [...(conversation?.participants ?? [])];
            if (participants && participantId) {
              const participantIndex = participants.findIndex(
                (participant) => participant.id === participantId
              );
              if (participantIndex !== -1) {
                participants.splice(participantIndex, 1, response);
              }
              this.updateConversation(conversationId, {
                participants: participants,
              });
            }
          }
          return response;
        } catch (error: unknown) {
          await errorStore.addErrorResponse(error, 'inline', true, 'editParticipant');
          return null;
        }
      },

      findParticipantInConversationByParticipantId(
        conversationId: string,
        participantId: string | null
      ): Participant | undefined {
        const conversation = store.entityMap()[conversationId];
        if (conversation?.participants && participantId) {
          return conversation.participants.find((participant) => participant.id === participantId);
        }
        return undefined;
      },

      findParticipantInConversationByUserId(
        conversationId: string,
        userId: string
      ): Participant | undefined {
        const conversation = store.entityMap()[conversationId];
        if (conversation?.participants) {
          return conversation.participants.find((participant) => participant.userId === userId);
        }
        return undefined;
      },

      async getConversationLink(
        conversationId: string,
        participantId: string
      ): Promise<string | null> {
        const conversation = store.entityMap()[conversationId];
        if (conversation?.clientFrontendUrls) {
          const participant = conversation.clientFrontendUrls.find(
            (clients) => clients.clientId === participantId
          );
          if (participant) {
            return participant.url;
          } else {
            const url = await this.fetchParticipantAuthUrl(conversationId, participantId);
            conversation.clientFrontendUrls.push({ clientId: participantId, url: url });
            return url;
          }
        } else {
          const participantClientUrlDto = await this.fetchParticipantAuthUrl(
            conversationId,
            participantId
          );
          this.updateConversation(conversationId, {
            clientFrontendUrls: [{ clientId: participantId, url: participantClientUrlDto }],
          });
          return participantClientUrlDto;
        }
      },

      async fetchParticipantAuthUrl(
        conversationId: string,
        participantId: string
      ): Promise<string | null> {
        const participantClientUrlDto =
          await wireApiService.participantsApi.getClientParticipantAuthUrl({
            conversationId,
            participantId,
          });
        return participantClientUrlDto.url ?? null;
      },
    };
  })
);

export type ActiveConversationStore = InstanceType<typeof ActiveConversationStore>;
